/**
 * TwoFactorGraceBanner - 2FA Grace Period Warnbanner
 * 
 * PR-3.1: Gap 9 - Legacy Parity
 * 
 * Zeigt Warnungen für Non-WORKER User die 2FA noch nicht aktiviert haben:
 * - >24h Restzeit: Info Banner (blau)
 * - ≤24h Restzeit: Warning Banner (orange)
 * 
 * Reine UI-Komponente - nutzt existierenden useAuth() Hook.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Info, X, ShieldCheck } from 'lucide-react';
import { useAuth } from '../App';

export function TwoFactorGraceBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  // Nicht anzeigen wenn: kein User, WORKER, 2FA aktiv, oder dismissed
  if (!user) return null;
  if (user.role === 'WORKER') return null;
  if (user.twoFactorEnabled) return null;
  if (dismissed) return null;

  // Grace Period aus User-Daten (falls vorhanden)
  // Wenn kein twoFactorGraceUntil im User-Objekt, nichts anzeigen
  const graceUntil = (user as any).twoFactorGraceUntil;
  if (!graceUntil) return null;

  const graceDate = new Date(graceUntil);
  const now = new Date();
  const remainingMs = graceDate.getTime() - now.getTime();

  // Bereits abgelaufen - kein Banner
  if (remainingMs <= 0) return null;

  const remainingHours = Math.ceil(remainingMs / (1000 * 60 * 60));
  const remainingDays = Math.ceil(remainingMs / (1000 * 60 * 60 * 24));
  const isUrgent = remainingHours <= 24;

  // Formatiertes Datum
  const deadlineFormatted = graceDate.toLocaleDateString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Verbleibende Zeit als Text
  const remainingText = isUrgent
    ? remainingHours <= 1
      ? 'weniger als 1 Stunde'
      : `${remainingHours} Stunden`
    : `${remainingDays} Tage`;

  return (
    <div
      className={`
        px-4 py-3 flex items-center justify-between gap-4
        ${isUrgent 
          ? 'bg-orange-100 border-b border-orange-200 text-orange-900' 
          : 'bg-blue-50 border-b border-blue-200 text-blue-900'
        }
      `}
    >
      <div className="flex items-center gap-3">
        {isUrgent ? (
          <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
        ) : (
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
        )}
        <div className="text-sm">
          {isUrgent ? (
            <span>
              <strong>2FA muss innerhalb von {remainingText} aktiviert werden.</strong>
              {' '}Frist: {deadlineFormatted}
            </span>
          ) : (
            <span>
              Zwei-Faktor-Authentifizierung ist noch nicht aktiv.
              {' '}Bitte bis <strong>{deadlineFormatted}</strong> einrichten.
              {' '}(Noch {remainingText})
            </span>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link to="/admin/profile">
          <button 
            className={`
              px-3 py-1.5 text-sm font-medium rounded-md flex items-center gap-1
              ${isUrgent 
                ? 'bg-orange-600 hover:bg-orange-700 text-white' 
                : 'border border-slate-300 bg-white hover:bg-slate-50 text-slate-700'
              }
            `}
          >
            <ShieldCheck className="w-4 h-4" />
            Jetzt einrichten
          </button>
        </Link>
        {!isUrgent && (
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 text-blue-700 hover:text-blue-900 hover:bg-blue-100 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
